import React, { useCallback, useEffect, useState } from "react";
import { Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatEastern } from "../lib/siteInfo";
import { LaunchCard, StatusBadge } from "../components/launch/ui";
import { TransferExport } from "../components/launch/TransferExport";
import { TransferImport } from "../components/launch/TransferImport";
import { EmployerImport } from "../components/launch/EmployerImport";

const runbook = (goLive) => [
  ["Two days before", [
    "Readiness checklist on this page shows all green (or each warning is understood).",
    "Latest financial feed and census loaded; employer-user spreadsheet imported; invitations queued.",
    "A fresh backup exists on the Backup page.",
  ]],
  ["Cut-over morning", [
    "In the hosting platform, open the production site's domain settings and remove prod-portal.myemployeesfirst.com.",
    "Add portal.myemployeesfirst.com as the production site's custom domain and note the DNS records shown.",
    "At the registrar, point portal.myemployeesfirst.com to those records (this ends the previous vendor's site).",
    "Update the production secrets FRONTEND_URL and ENTRA_REDIRECT_URI to https://portal.myemployeesfirst.com, then re-publish with Replace.",
    "Wait until the address and its certificate show as verified, then open https://portal.myemployeesfirst.com and sign in with Microsoft 365.",
  ]],
  [`Go-live (${formatEastern(goLive)})`, [
    "Go-live fires automatically: real email starts, employer users can sign in and queued invitations are sent.",
    "If the domain is not verified in time, postpone go-live in Settings → Go live before the scheduled moment.",
    "Sign in as a test employer user from an invitation and confirm the dashboard loads.",
  ]],
  ["After go-live", [
    "Watch the Audit page and Blumira for sign-in errors during the first hours.",
    "Confirm the nightly backup ran the next morning.",
    "Leave dev-portal as the build and test site; promote later updates with the Release checklist.",
  ]],
];

function Readiness({ data, onRefresh }) {
  return (
    <LaunchCard eyebrow={`Cut-over readiness · ${data.environment} · v${data.build.version}`} title={`${data.summary.pass} pass · ${data.summary.warn} warning · ${data.summary.fail} not ready`} testId="readiness-card"
      aside={<button type="button" className="btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1.5 print:hidden" onClick={onRefresh} data-testid="readiness-refresh-btn"><RefreshCw size={12} /> Re-check</button>}>
      <ul>
        {data.checks.map((c) => (
          <li key={c.key} className="flex items-start justify-between gap-3 border-b py-3 last:border-b-0" style={{ borderColor: "var(--border)" }} data-testid={`readiness-check-${c.key}`}>
            <div className="text-sm min-w-0 flex-1">
              <div className="font-semibold" style={{ color: "var(--ink)" }}>{c.label}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{c.detail}</div>
            </div>
            <StatusBadge status={c.status} testId={`readiness-status-${c.key}`} />
          </li>
        ))}
      </ul>
    </LaunchCard>
  );
}

function Runbook({ goLive }) {
  return (
    <LaunchCard eyebrow={formatEastern(goLive)} title="Cut-over day runbook" testId="runbook-card"
      aside={<button type="button" className="btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1.5 print:hidden" onClick={() => window.print()} data-testid="runbook-print-btn"><Printer size={12} /> Print</button>}>
      {runbook(goLive).map(([phase, steps]) => (
        <div key={phase} className="mb-4">
          <div className="eyebrow mb-1">{phase}</div>
          <ol className="list-decimal pl-5 text-sm space-y-1" style={{ color: "var(--ink)" }}>
            {steps.map((s) => <li key={s}><span className="inline-block w-3 h-3 border mr-2 align-middle" style={{ borderColor: "var(--border-strong)" }} />{s}</li>)}
          </ol>
        </div>
      ))}
    </LaunchCard>
  );
}

export default function AdminLaunch() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/cutover/readiness").then(({ data: d }) => setData(d))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  if (!user?.permissions?.includes("system")) {
    return <div className="mx-auto max-w-[1400px] px-6 py-16 text-sm" style={{ color: "var(--muted)" }} data-testid="admin-launch-forbidden">System admins only.</div>;
  }
  if (!data) return <div className="p-10 eyebrow">Loading…</div>;
  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10" data-testid="admin-launch-page">
      <div className="eyebrow mb-2">{data.environment === "production" ? "Production" : `${data.environment} (rehearsal)`} · go-live {formatEastern(data.golive.go_live_at)}</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>Cut-over</h1>
      <p className="text-sm max-w-3xl mb-8" style={{ color: "var(--muted)" }}>
        Load production, work the checklist to green and keep the runbook to hand on cut-over day.
      </p>
      <div className="grid gap-6 xl:grid-cols-2 mb-10">
        <Readiness data={data} onRefresh={load} />
        <Runbook goLive={data.golive.go_live_at} />
      </div>
      <h2 className="font-heading font-bold text-2xl mb-4 print:hidden" style={{ color: "var(--ink)" }}>Load production data</h2>
      <div className="grid gap-6 xl:grid-cols-2 print:hidden">
        <TransferExport />
        <TransferImport environment={data.environment} />
        <div className="xl:col-span-2"><EmployerImport /></div>
      </div>
    </div>
  );
}
